import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Button, IconButton } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import ClearIcon from "@material-ui/icons/Delete";
import PropTypes from "prop-types";
import styled from "styled-components";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const Container = styled.div`
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
`;

const SubLabel = styled.div`
  font-size: 14px;
  color: #718096;
  margin-bottom: 16px;
`;

const DropzoneContainer = styled.div`
  position: relative;
  border: 2px dashed ${props =>
    props.isDragActive
      ? props.isDragAccept ? '#48bb78' : '#f56565'
      : '#e2e8f0'
  };
  border-radius: 12px;
  background: ${props =>
    props.isDragActive
      ? props.isDragAccept ? '#f0fff4' : '#fed7d7'
      : '#fafafa'
  };
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    border-color: #cbd5e0;
    background: #f7fafc;
  }
  
  &:focus-within {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

const UploadIcon = styled(CloudUploadIcon)`
  font-size: 48px !important;
  color: #a0aec0;
  margin-bottom: 16px;
`;

const DropzoneText = styled.div`
  font-size: 16px;
  color: #4a5568;
  margin-bottom: 8px;
  font-weight: 500;
`;

const DropzoneSubtext = styled.div`
  font-size: 14px;
  color: #718096;
`;

const FileList = styled.div`
  margin-bottom: 16px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const FilePreview = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const FileType = styled.span`
  background: #edf2f7;
  color: #4a5568;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const FileName = styled.span`
  flex: 1;
  color: #2d3748;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButton = styled(IconButton)`
  padding: 6px !important;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
`;

const DownloadButton = styled(ActionButton)`
  color: #4299e1 !important;
  
  &:hover {
    background-color: rgba(66, 153, 225, 0.08) !important;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #f56565 !important;
  
  &:hover {
    background-color: rgba(245, 101, 101, 0.08) !important;
  }
`;

const ChangeButton = styled(Button)`
  margin-top: 12px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  padding: 8px 24px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-1px);
  }
`;

const LoadingFileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 6px;
`;

const LoadingFileName = styled.span`
  color: #4a5568;
  font-size: 14px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const filesString = "application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.presentationml.slideshow";

export default function StyledDropzone(props) {
	console.log({props})
	// `files` from props is for files being uploaded, not yet saved.
	const files = React.useMemo(() => {
		if (props.files) {
			if (Array.isArray(props.files)) return props.files.filter(f => f.name === props.name);
			else if (props.files.name === props.name) return [props.files]; // Always treat as an array for consistency
			else return [];
		} return [];
	}, [props.files, props.name]);

	const [changing, setChanging] = useState(false);

	const onDrop = useCallback((acceptedFiles) => {
		if (acceptedFiles.length > 0) {
			if (props.onFile) {
				props.onFile({ files: acceptedFiles }, props.name, props.single);
			}
			// If it's a single file upload, we are no longer in the "changing" state
			// after a file has been selected.
			if (!props.multipleFiles) {
				setChanging(false);
			}
		}
	}, [props.onFile, props.name, props.single, props.multipleFiles]);

	const {
		getRootProps,
		getInputProps,
		isDragActive,
		isDragAccept,
		isDragReject
	} = useDropzone({
		accept: props.accept ? props.accept : (props.acceptFiles ? filesString : filesString),
		onDrop,
	});
	
	// `value` is for files that are already saved and passed from the parent.
	const value = Array.isArray(props.value) ? props.value : (props.value ? [props.value] : []);

	console.log({value})
	const handleRemove = (idx) => {
		if (!props.onRemove || !props.value) return;

		const updatedFiles = [...value];
		updatedFiles.splice(idx, 1);

		if (!props.multipleFiles) {
			props.onRemove(null);
		} else {
			props.onRemove(updatedFiles);
		}
	};

	const isImageFile = (file) => {
		const type = file.type || file.tipo || '';
		return type.startsWith("image/");
	};

	const getFileExtension = (file) => {
		const type = file.type || file.tipo || '';
		const lowerType = type.toLowerCase();
		if (lowerType.includes('pdf')) return 'pdf';
		if (lowerType.includes('word') || lowerType.includes('doc')) return 'doc';
		if (lowerType.includes('excel') || lowerType.includes('sheet')) return 'xls';
		if (lowerType.includes('powerpoint') || lowerType.includes('presentation')) return 'ppt';
		if (lowerType.includes('text')) return 'txt';
		if (lowerType.startsWith('image/')) return 'img';
		
		const name = file.name || '';
		const extension = name.split('.').pop();
		return extension || 'file';
	};
    
	// Simplified logic for when to show the dropzone
	const showDropzone = !props.readOnly && (
		props.multipleFiles ||                      // Always show if multiple files are allowed
		(value.length === 0 && files.length === 0) || // Show if there are no files yet
		changing                                    // Show if user clicks "Change"
	);

	return (
		<Container>
			{props.label && <Label>{props.label}</Label>}
			{props.sublabel && <SubLabel>{props.sublabel}</SubLabel>}

			{/* Existing (saved) files */}
			{value.length > 0 && (
				<FileList>
					{value.map((file, idx) => (
						<FileItem key={`value-${idx}`}>
							{(isImageFile(file) && !props.hideFile) ? (
								<FilePreview
									src={`${process.env.REACT_APP_FILE_URL}${file.path}`}
									alt={file.name}
								/>
							) : (
								<FileType>{getFileExtension(file)}</FileType>
							)}

							<FileName>{file.name || `File ${idx + 1}`}</FileName>

							{props.download && (
								<DownloadButton
									size="small"
									component="a"
									target="_blank"
									rel="noopener noreferrer"
									href={`${process.env.REACT_APP_FILE_URL}${file.path}`}
									download
								>
									<GetAppIcon fontSize="small" />
								</DownloadButton>
							)}

							{(props.onRemove && props.value) && (
								<DeleteButton
									size="small"
									onClick={() => handleRemove(idx)}
								>
									<DeleteOutlineIcon fontSize="small" />
								</DeleteButton>
							)}
						</FileItem>
					))}
				</FileList>
			)}

			{/* Loading/Staged files (from props.files) */}
			{files.length > 0 && (
				<FileList>
					{files.map((file, idx) => (
						<LoadingFileItem key={`loading-${idx}`}>
							<ClearIcon
								onClick={() => props.removeFile && props.removeFile(file.file)}
								fontSize="small"
								style={{ color: "#f56565", cursor: "pointer" }}
							/>
							<LoadingFileName>{file.file.name}</LoadingFileName>
						</LoadingFileItem>
					))}
				</FileList>
			)}

			{/* Change button (only for single file mode when a file exists) */}
			{!props.readOnly && value.length > 0 && !props.multipleFiles && !changing && (
				<ChangeButton
					variant="contained"
					size="small"
					onClick={() => setChanging(true)}
				>
					Cambiar archivo
				</ChangeButton>
			)}

			{/* Dropzone */}
			{showDropzone && (
				<DropzoneContainer
					{...getRootProps()}
					isDragActive={isDragActive}
					isDragAccept={isDragAccept}
					isDragReject={isDragReject}
				>
					<input {...getInputProps()} />
					<UploadIcon />
					<DropzoneText>
						{isDragActive
							? (isDragAccept ? "Suelta los archivos aqui" : "Tipo de archivo no soportado")
							: "Da clic para seleccionar archivos"
						}
					</DropzoneText>
					<DropzoneSubtext>
						{props.multipleFiles ? "Multiples archivos" : "Máximo 1 archivo"}
					</DropzoneSubtext>
				</DropzoneContainer>
			)}
		</Container>
	);
}

StyledDropzone.propTypes = {
	onFile: PropTypes.func,
	name: PropTypes.string,
	single: PropTypes.bool,
	accept: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	acceptFiles: PropTypes.bool,
	circle: PropTypes.bool,
	value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.number, PropTypes.string]),
	download: PropTypes.bool,
	files: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	onRemove: PropTypes.func,
	removeFile: PropTypes.func,
	label: PropTypes.string,
	sublabel: PropTypes.string,
	readOnly: PropTypes.bool,
	multipleFiles: PropTypes.bool,
	hideFile: PropTypes.bool
};